import { FeatureKey } from 'common/constants/permission'
import { CRUDAction } from 'common/constants/permission'
import { formatThaiShortDateTwoDigitYear, getAgeOnDate } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useGetCycleById } from 'modules/pond/api/queries'
import { useGetPondNameById, useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { SpeciesName } from 'modules/pond/constants'
import { PartialTransfer } from 'modules/shrimpTransfer/api/types'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import DeleteIcon from '@mui/icons-material/Delete'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
} from '@mui/material'

interface ShrimpTransferViewDialogProps {
  open: boolean
  onClose: () => void
  data: PartialTransfer
  onEdit?: () => void
  onDelete?: (id: string) => void
}

const ShrimpTransferViewDialog = ({
  open,
  onClose,
  data,
  onEdit,
  onDelete,
}: ShrimpTransferViewDialogProps) => {
  const { t } = useTranslation('shrimpTransfer')
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId || '')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const { data: cycleInfo } = useGetCycleById()
  // The API's `age` is the cycle's age today, not on the transfer date, so
  // derive it from the cycle start (DOF-1348); fall back to the API value.
  const ageOnTransfer = getAgeOnDate(cycleInfo?.startDate, data?.transfer_date) ?? data?.age
  const { t: tPond } = useTranslation('pond')

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 0 },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={700} fontSize={20} color="primary.main">
              {t('transfer.record')}
              {`${species}`} {t('transfer.pond')} {pondInfo?.name}
            </Typography>
            {data?.type === 'out' && (
              <MoreAction
                featureKey={FeatureKey.ShrimpTransfer}
                onClickOptions={[
                  {
                    icon: <DeleteIcon />,
                    text: tPond('delete'),
                    action: data?.id ? () => onDelete?.(String(data?.id)) : undefined,
                    crudIdx: CRUDAction.Delete,
                  },
                ]}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography fontWeight={600} fontSize={16}>
              {formatThaiShortDateTwoDigitYear(data?.transfer_date || '', { showTime: true })}
            </Typography>
            {!!ageOnTransfer && (
              <Typography color="text.secondary" fontSize={15}>
                {t('transfer.pondAge', { species })} {ageOnTransfer} {t('transfer.dateUnit')}
              </Typography>
            )}
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography color="text.secondary" fontSize={15}>
                {t('transfer.from')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={500} fontSize={16} textAlign="right">
                {data?.source_pond_name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary" fontSize={15}>
                {t('transfer.toPond')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={500} fontSize={16} textAlign="right">
                {data?.destination_pond_name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary" fontSize={15}>
                {t('transfer.shrimpSize', { species })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={500} fontSize={16} textAlign="right">
                {fNumber(data?.amount_per_kg)}{' '}
                <span style={{ color: '#B0BEC5', fontWeight: 400 }}>
                  ({t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')})
                </span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary" fontSize={15}>
                {t('transfer.quantity')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={500} fontSize={16} textAlign="right">
                {fNumber(data?.amount)}{' '}
                <span style={{ color: '#B0BEC5', fontWeight: 400 }}>({t('transfer.kg')})</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary" fontSize={15}>
                {isFish ? t('transfer.cost.per.one') : t('transfer.cost.per.kg')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={500} fontSize={16} textAlign="right">
                {fNumber(data?.cost_per_kg)}{' '}
                <span style={{ color: '#B0BEC5', fontWeight: 400 }}>({t('transfer.baht')})</span>
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={onClose}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('transfer.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ShrimpTransferViewDialog
